import { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useMayaSidebar } from "../context/use-maya-sidebar";
import {
  centreOf,
  createStars,
  starAt,
  warpDuration,
  type Point,
  type WarpStar,
} from "../utils/warp";

const STAR_COUNT = 18;
/** Reveal the destination once this share of the swarm's flight is over. */
const LAND_AT = 0.8;
/** The shockwave ring at the destination after landing. */
const SHOCKWAVE_MS = 520;
/** The flash where the source implodes. */
const IMPLODE_MS = 240;
/** Comet tail length, in frames. */
const TRAIL = 7;
/** Past the expected end, give up and reveal both ends anyway (a background
 *  tab pauses requestAnimationFrame; nothing may stay hidden because of it). */
const SAFETY_MARGIN_MS = 1000;

// Used where a canvas can't parse the theme's oklch() tokens.
const FALLBACK_COLORS = ["#6438ff", "#e2489a", "#3b82f6", "#a78bfa"];

/** Brand token colours, as the canvas understands them. */
const readPalette = (ctx: CanvasRenderingContext2D): string[] => {
  const css = getComputedStyle(document.documentElement);
  return [
    "--color-primary",
    "--color-accent",
    "--color-info",
    "--color-primary",
  ].map((token, i) => {
    const value = css.getPropertyValue(token).trim();
    ctx.fillStyle = "#000000";
    if (value) ctx.fillStyle = value;
    // Assignment is silently ignored when the colour can't be parsed.
    return value && ctx.fillStyle !== "#000000" ? value : FALLBACK_COLORS[i];
  });
};

/** Four-point sparkle, centred on the origin. */
const sparklePath = (ctx: CanvasRenderingContext2D, r: number) => {
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.quadraticCurveTo(0, 0, 0, r);
  ctx.quadraticCurveTo(0, 0, -r, 0);
  ctx.quadraticCurveTo(0, 0, 0, -r);
  ctx.closePath();
};

const ring = (
  ctx: CanvasRenderingContext2D,
  at: Point,
  radius: number,
  alpha: number,
  color: string,
  width: number,
) => {
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.arc(at.x, at.y, radius, 0, Math.PI * 2);
  ctx.stroke();
};

/**
 * The "stardust warp": opening Maya bursts the launcher into a swarm of
 * sparkles that fly on bowed comet paths into the sidebar's avatar — which
 * forms where they land — and closing sends them back (or, on arriving at
 * the AI Assistant page, into that page's composer). One full-screen canvas,
 * mounted only while a warp runs; the provider skips the warp entirely under
 * `prefers-reduced-motion`.
 *
 * The destination is re-measured every frame: the sidebar is still sliding
 * open while the stars fly, and they curve after it instead of landing where
 * it used to be.
 */
export const MayaWarpOverlay = () => {
  const {
    warp,
    landWarp,
    clearWarp,
    triggerRef,
    headerOrbRef,
    assistantComposerRef,
  } = useMayaSidebar();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Read once per warp — the effect below keys on the id alone, so the
  // landed-phase update doesn't restart the animation. Synced in a layout
  // effect, which runs before that effect in the same commit.
  const warpRef = useRef(warp);
  useLayoutEffect(() => {
    warpRef.current = warp;
  }, [warp]);
  const warpId = warp?.id;

  useEffect(() => {
    const current = warpRef.current;
    if (!current) return;
    const { id, direction, from } = current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      // No canvas, no show — but never leave the launcher or avatar hidden.
      clearWarp(id);
      return;
    }

    // 1x even on retina: the stars are soft glows anyway, and a full-viewport
    // 2x canvas is ~60MB of backing store on a 1440p display.
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w;
    canvas.height = h;

    const palette = readPalette(ctx);
    const stars: WarpStar[] = createStars(STAR_COUNT, palette);
    const trails: Point[][] = stars.map(() => []);
    const flight = warpDuration(stars);
    const landAt = flight * LAND_AT;
    const end = Math.max(flight, landAt + SHOCKWAVE_MS);

    const destination = () =>
      direction === "in"
        ? headerOrbRef.current
        : direction === "page"
          ? (assistantComposerRef.current?.inputRef.current ?? null)
          : triggerRef.current;
    // Until the destination has mounted, aim where it will be — the
    // header's corner of the screen, or the page's composer at the bottom;
    // the first real measurement takes over.
    const rtl = document.documentElement.dir === "rtl";
    let target: Point =
      direction === "page"
        ? { x: w / 2, y: h - 90 }
        : { x: rtl ? 60 : w - 60, y: 70 };

    let landed = false;
    let raf = 0;
    const start = performance.now();

    const frame = (now: number) => {
      const t = now - start;
      target = centreOf(destination()) ?? target;
      ctx.clearRect(0, 0, w, h);

      // The source implodes in a soft flash as the stars leave it.
      if (t < IMPLODE_MS) {
        const e = t / IMPLODE_MS;
        const glow = ctx.createRadialGradient(
          from.x,
          from.y,
          0,
          from.x,
          from.y,
          34,
        );
        glow.addColorStop(0, palette[0]);
        glow.addColorStop(1, "transparent");
        ctx.globalAlpha = 0.45 * (1 - e);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(from.x, from.y, 12 + 22 * e, 0, Math.PI * 2);
        ctx.fill();
      }

      stars.forEach((star, i) => {
        const f = starAt(star, t, from, target);
        const trail = trails[i];
        if (!f) {
          trail.length = 0;
          return;
        }
        trail.push({ x: f.x, y: f.y });
        if (trail.length > TRAIL) trail.shift();

        // Comet tail: tapering, fading segments through recent positions.
        ctx.lineCap = "round";
        ctx.strokeStyle = star.color;
        for (let k = 1; k < trail.length; k++) {
          const weight = k / trail.length;
          ctx.globalAlpha = f.alpha * weight * 0.45;
          ctx.lineWidth = star.size * f.scale * weight * 1.1;
          ctx.beginPath();
          ctx.moveTo(trail[k - 1].x, trail[k - 1].y);
          ctx.lineTo(trail[k].x, trail[k].y);
          ctx.stroke();
        }

        // The star itself, over a faint halo (a plain fill — shadowBlur on
        // every star, every frame, is far more expensive).
        ctx.fillStyle = star.color;
        ctx.globalAlpha = f.alpha * 0.22;
        ctx.beginPath();
        ctx.arc(f.x, f.y, star.size * 2.4 * f.scale, 0, Math.PI * 2);
        ctx.fill();

        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rotation);
        ctx.globalAlpha = f.alpha;
        if (star.shape === "sparkle") {
          sparklePath(ctx, star.size * 2 * f.scale);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, star.size * 0.8 * f.scale, 0, Math.PI * 2);
        }
        ctx.fill();
        ctx.restore();
      });

      if (!landed && t >= landAt) {
        landed = true;
        landWarp(id);
      }

      // Shockwave where the swarm landed: two rings rolling outward.
      if (t >= landAt) {
        const e = Math.min(1, (t - landAt) / SHOCKWAVE_MS);
        ring(ctx, target, 14 + 34 * e, 0.55 * (1 - e), palette[0], 2);
        ring(ctx, target, 10 + 22 * e, 0.35 * (1 - e), palette[1], 1.5);
      }
      ctx.globalAlpha = 1;

      if (t < end) {
        raf = requestAnimationFrame(frame);
      } else {
        ctx.clearRect(0, 0, w, h);
        clearWarp(id);
      }
    };

    raf = requestAnimationFrame(frame);
    const safety = window.setTimeout(
      () => clearWarp(id),
      end + SAFETY_MARGIN_MS,
    );
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(safety);
    };
  }, [
    warpId,
    landWarp,
    clearWarp,
    triggerRef,
    headerOrbRef,
    assistantComposerRef,
  ]);

  if (!warp) return null;
  return createPortal(
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      // Above the launcher and the sidebar (z-45), below modals (z-50).
      className="pointer-events-none fixed inset-0 z-46 h-screen w-screen"
    />,
    document.body,
  );
};
